// src/lib/article-meta.ts

export interface ArticleMeta {
  lang: 'en' | 'fa' | 'tr' | 'ar';
  authorId?: string;
  [key: string]: unknown;
}

export interface ArticleData {
  id?: string | number;
  slug?: string;
  title?: string;
  content?: string;
  excerpt?: string;
  authorId?: string;
  author_id?: string;
  authorName?: string;
  author_name?: string;
  author?: string;
  audioUrl?: string;
  audio_url?: string;
  created_at?: string;
  language?: SupportedArticleLang;
  languageInfo?: { id: string; label: string; flag: string; dir: string };
  [key: string]: unknown;
}

export const SUPPORTED_ARTICLE_LANGS = [
  { id: 'en', label: 'English', flag: '🇺🇸', dir: 'ltr' },
  { id: 'fa', label: 'فارسی', flag: '🇮🇷', dir: 'rtl' },
  { id: 'tr', label: 'Türkçe', flag: '🇹🇷', dir: 'ltr' },
  { id: 'ar', label: 'العربية', flag: '🇸🇦', dir: 'rtl' },
] as const;

export type SupportedArticleLang = 'en' | 'fa' | 'tr' | 'ar';

/**
 * Generates or retrieves a unique persistent token for the current writer on this browser device.
 * Even without email authentication, this identifies the creator of an article on this device.
 */
export function getLocalAuthorId(): string {
  if (typeof window === 'undefined') return 'server_author';
  try {
    let id = localStorage.getItem('green_campaign_writer_token');
    if (!id) {
      id = 'wr_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now();
      localStorage.setItem('green_campaign_writer_token', id);
    }
    return id;
  } catch {
    return 'anon_writer';
  }
}

/**
 * Registers an authored article slug or ID in localStorage so the writer can later edit or delete it.
 */
export function addAuthoredArticle(identifier: string) {
  if (typeof window === 'undefined' || !identifier) return;
  try {
    const existing = JSON.parse(localStorage.getItem('my_authored_articles') || '[]');
    if (!existing.includes(identifier)) {
      existing.push(identifier);
      localStorage.setItem('my_authored_articles', JSON.stringify(existing));
    }
  } catch {
    // Ignore storage errors
  }
}

/**
 * Removes an authored article slug or ID from localStorage after deletion.
 */
export function removeAuthoredArticle(identifier: string) {
  if (typeof window === 'undefined' || !identifier) return;
  try {
    let existing: string[] = JSON.parse(localStorage.getItem('my_authored_articles') || '[]');
    existing = existing.filter(id => id !== identifier && id !== String(identifier));
    localStorage.setItem('my_authored_articles', JSON.stringify(existing));
  } catch {}
}

/**
 * Checks if the currently viewing user is authorized to edit or delete this article.
 */
export function isArticleAuthor(article: ArticleData | null, sessionUser?: unknown): boolean {
  if (!article) return false;
  
  // 1. If logged in with Supabase auth as admin or authenticated user
  if (sessionUser) return true;
  
  if (typeof window === 'undefined') return false;

  // 2. Check if the local writer token matches the article authorId
  const localAuthor = getLocalAuthorId();
  if (article.authorId && article.authorId === localAuthor) {
    return true;
  }

  // 3. Check if the slug or ID exists in localStorage authored list
  try {
    const myArticles: string[] = JSON.parse(localStorage.getItem('my_authored_articles') || '[]');
    if (myArticles.includes(article.slug as string) || myArticles.includes(article.id as string) || myArticles.includes(String(article.id))) {
      return true;
    }
  } catch {}

  return false;
}

/**
 * Embeds language and author ID metadata directly at the beginning of article HTML content.
 * This guarantees zero-schema-error persistence across any Supabase table configuration.
 */
export function embedArticleMeta(rawContent: string, lang: SupportedArticleLang, authorId?: string): string {
  // First clean any existing meta tag
  const { content } = parseArticleMeta({ content: rawContent });
  const metaObj = { lang, authorId: authorId || getLocalAuthorId() };
  return `<!--meta:${JSON.stringify(metaObj)}-->\n${content}`;
}

/**
 * Parses raw article object from Supabase, extracting clean content, language, and authorId.
 */
export function parseArticleMeta(rawArticle: ArticleData): ArticleData & { lang: SupportedArticleLang, languageInfo: typeof SUPPORTED_ARTICLE_LANGS[number] } {
  if (!rawArticle) return rawArticle;
  
  let content = rawArticle.content || '';
  let lang: SupportedArticleLang = rawArticle.language || 'en';
  let authorId = rawArticle.author_id || rawArticle.authorId || undefined;

  const metaRegex = /^\s*<!--meta:(\{.*?\})-->\s*/;
  const match = content.match(metaRegex);
  
  if (match && match[1]) {
    try {
      const parsedMeta = JSON.parse(match[1]);
      if (parsedMeta.lang && ['en', 'fa', 'tr', 'ar'].includes(parsedMeta.lang)) {
        if (!rawArticle.language) {
          lang = parsedMeta.lang;
        }
      }
      if (parsedMeta.authorId && !authorId) {
        authorId = parsedMeta.authorId;
      }
      // Remove the comment tag from display content
      content = content.replace(metaRegex, '');
    } catch {}
  }

  return {
    ...rawArticle,
    content: content.trim(),
    lang,
    authorId,
    languageInfo: SUPPORTED_ARTICLE_LANGS.find(l => l.id === lang) || SUPPORTED_ARTICLE_LANGS[0],
  };
}
